import React, { useMemo, useState } from 'react';

import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';

import { MarginOrdersType } from 'src/lib/margin/types';

import { MarginBalanceAdjustmentsTable } from './Tables/MarginBalanceAdjustmentsTable';
import { MarginHistoryTable, MarginHistoryTableData } from './Tables/MarginHistoryTable';
import { MarginPositionsTable, MarginPositionsTableData } from './Tables/MarginPositionsTable';
import { MarginTransactionsTable } from './Tables/MarginTransactionsTable';
import { MarginPositionsTableTabs } from './components/MarginPositionsTableTabs';
import { useMarginPositionsTableFilterStore } from './useMarginPositionsTableFilterStore';

type Props = {
  groupBy?: keyof MarginPositionsTableData | keyof MarginHistoryTableData;
  actions?: React.ReactNode;
};

const MarginPositionsCard: React.FC<Props> = ({ groupBy, actions }) => {
  const { showFilters } = useMarginPositionsTableFilterStore();
  const [tab, setTab] = useState<MarginOrdersType>(MarginOrdersType.Position);
  const isDesktop = useTailwindBreakpoint('md', true);
  const isSm = useTailwindBreakpoint('sm');
  const isXs = useTailwindBreakpoint('xs');

  const content = useMemo(() => {
    switch (tab) {
      case MarginOrdersType.History:
        return (
          <MarginHistoryTable
            showFilter={showFilters}
            hideHeader={isXs}
            isSm={isSm}
            isXs={isXs}
            groupBy={groupBy as keyof MarginHistoryTableData}
          />
        );
      case MarginOrdersType.Position:
      case MarginOrdersType.Orders:
        return (
          <MarginPositionsTable
            ordersType={tab}
            showFilter={showFilters}
            hideHeader={isXs}
            isSm={isSm}
            isXs={isXs}
            groupBy={groupBy as keyof MarginPositionsTableData}
          />
        );
      case MarginOrdersType.Transfers:
        return (
          <MarginTransactionsTable
            groupBy={isDesktop ? undefined : 'assetTransferStatusDate'}
            isDesktopView={isDesktop}
            hideHeader={isXs}
            isSm={isSm}
            isXs={isXs}
          />
        );
      case MarginOrdersType.BalanceAdjustments:
        return (
          <MarginBalanceAdjustmentsTable
            groupBy={isDesktop ? undefined : 'adjustmentDate'}
            isDesktopView={isDesktop}
            hideHeader={isXs}
            isSm={isSm}
            isXs={isXs}
          />
        );
      default:
        tab satisfies never;
    }
  }, [groupBy, isDesktop, isSm, isXs, showFilters, tab]);

  return (
    <Card className='h-full w-full rounded-[12px] md:overflow-hidden'>
      <FlexLayout
        className='hide-scrollbar-all sticky top-0 z-10 w-full overflow-x-auto rounded-t-16 border-b border-color-border-main bg-color-background-surface-primary px-12 py-2 md:py-4'
        alignItems='center'
        justifyContent='space-between'
        spacing={24}
      >
        <MarginPositionsTableTabs tab={tab} setTab={setTab} />
        {[MarginOrdersType.History, MarginOrdersType.Orders, MarginOrdersType.Position].includes(tab) && actions}
      </FlexLayout>
      <FlexLayout className='h-full w-full overflow-auto rounded-b-16 md:overflow-hidden'>{content}</FlexLayout>
    </Card>
  );
};

export { MarginPositionsCard };
